import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  DraftPair,
  DraftPlayer,
  DraftSide,
  DraftSideChoice,
} from "../types/challengeDraft";

export function normalizeCpf(value: unknown) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, 11);
}

export function normalizeCpfFromPlayerInput(value: unknown) {
  const text = String(value || "").trim();
  const trailingCpf = text.match(/(\d[\d.\-\s]{7,}\d)\s*$/);
  if (trailingCpf?.[1]) return normalizeCpf(trailingCpf[1]);
  return normalizeCpf(text);
}

export function createPairId(cpf1: string, cpf2: string) {
  return [cpf1, cpf2].sort().join("-");
}

export function normalizePairId(value: unknown) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const parts = raw.split("-");
  if (parts.length !== 2) return raw;
  const cpf1 = normalizeCpf(parts[0]);
  const cpf2 = normalizeCpf(parts[1]);
  if (!cpf1 || !cpf2 || cpf1 === cpf2) return raw;
  return createPairId(cpf1, cpf2);
}

export function shuffleList<T>(items: T[]) {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[other]] = [copy[other], copy[index]];
  }
  return copy;
}

export function normalizeDraftSideChoice(
  value: unknown,
): DraftSideChoice | null {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (
    normalized === "direita" ||
    normalized === "esquerda" ||
    normalized === "ambos"
  ) {
    return normalized;
  }
  return null;
}

export function sideChoiceToDraftSide(
  value: unknown,
  options?: { preferenceMissing?: boolean },
): DraftSide {
  const choice = normalizeDraftSideChoice(value);
  if (choice === "direita") {
    return {
      direita: true,
      esquerda: false,
      preferenceMissing: Boolean(options?.preferenceMissing),
    };
  }
  if (choice === "esquerda") {
    return {
      direita: false,
      esquerda: true,
      preferenceMissing: Boolean(options?.preferenceMissing),
    };
  }
  return {
    direita: true,
    esquerda: true,
    preferenceMissing: Boolean(options?.preferenceMissing),
  };
}

export function draftSideToChoice(side: DraftSide): DraftSideChoice | null {
  const right = Boolean(side?.direita);
  const left = Boolean(side?.esquerda);
  if (right && left) return "ambos";
  if (right) return "direita";
  if (left) return "esquerda";
  return null;
}

export function buildDrawPairs(players: DraftPlayer[]) {
  const valid = players
    .filter((item) => item && item.cpf)
    .map((item) => ({
      ...item,
      side: {
        direita: Boolean(item.side?.direita),
        esquerda: Boolean(item.side?.esquerda),
      },
    }));

  if (valid.length % 2 !== 0) {
    return {
      ok: false as const,
      error: siteTexts.challengeEngine.requiresEvenPlayers,
    };
  }

  const leftOnly = shuffleList(
    valid.filter((p) => p.side.esquerda && !p.side.direita),
  );
  const rightOnly = shuffleList(
    valid.filter((p) => p.side.direita && !p.side.esquerda),
  );
  const both = shuffleList(
    valid.filter((p) => p.side.direita && p.side.esquerda),
  );

  const pairs: Array<[DraftPlayer, DraftPlayer]> = [];
  let sideConflict = false;

  while (leftOnly.length > 0 && rightOnly.length > 0) {
    pairs.push([leftOnly.pop()!, rightOnly.pop()!]);
  }

  if (leftOnly.length > rightOnly.length) {
    while (leftOnly.length > 0 && both.length > 0) {
      pairs.push([leftOnly.pop()!, both.pop()!]);
    }
  } else if (rightOnly.length > leftOnly.length) {
    while (rightOnly.length > 0 && both.length > 0) {
      pairs.push([both.pop()!, rightOnly.pop()!]);
    }
  }

  while (leftOnly.length >= 2) {
    sideConflict = true;
    pairs.push([leftOnly.pop()!, leftOnly.pop()!]);
  }
  while (rightOnly.length >= 2) {
    sideConflict = true;
    pairs.push([rightOnly.pop()!, rightOnly.pop()!]);
  }
  while (both.length >= 2) {
    pairs.push([both.pop()!, both.pop()!]);
  }

  if (leftOnly.length || rightOnly.length || both.length) {
    return {
      ok: false as const,
      error: siteTexts.challengeEngine.unableToBuildPairs,
    };
  }

  return { ok: true as const, pairs: shuffleList(pairs), sideConflict };
}

export function buildChallengeRoundsFromPairs(pairs: DraftPair[], courts = 2) {
  const COURTS = Math.max(1, Math.floor(Number(courts || 2)));
  const MAX_GAMES_IN_ROW = 2;
  const MAX_RESTS_IN_ROW = 1;
  const pairIds = pairs.map((pair) => pair.id).filter(Boolean);
  if (pairIds.length < 2) return [];

  type Match = {
    dupla1Id: string;
    dupla2Id: string;
    score1: number | "";
    score2: number | "";
  };

  const buildAllMatches = (ids: string[]) => {
    const out: Match[] = [];
    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        out.push({
          dupla1Id: ids[i],
          dupla2Id: ids[j],
          score1: "",
          score2: "",
        });
      }
    }
    return out;
  };

  const matchesCompatible = (m1: Match, m2: Match) =>
    m1.dupla1Id !== m2.dupla1Id &&
    m1.dupla1Id !== m2.dupla2Id &&
    m1.dupla2Id !== m2.dupla1Id &&
    m1.dupla2Id !== m2.dupla2Id;

  const updateState = (
    state: Record<string, { play: number; idle: number }>,
    ids: string[],
    playing: Set<string>,
  ) => {
    const next: Record<string, { play: number; idle: number }> = {};
    for (const id of ids) {
      const currentlyPlaying = playing.has(id);
      const gamesInRow = currentlyPlaying ? state[id].play + 1 : 0;
      const restsInRow = currentlyPlaying ? 0 : state[id].idle + 1;
      if (gamesInRow > MAX_GAMES_IN_ROW || restsInRow > MAX_RESTS_IN_ROW)
        return null;
      next[id] = { play: gamesInRow, idle: restsInRow };
    }
    return next;
  };

  const tryBuildRounds = (
    matches: Match[],
    ids: string[],
    courts: number,
    totalRounds: number,
  ) => {
    const initial: Record<string, { play: number; idle: number }> = {};
    ids.forEach((id) => {
      initial[id] = { play: 0, idle: 0 };
    });

    const backtrack = (
      roundIndex: number,
      remaining: Match[],
      currentState: Record<string, { play: number; idle: number }>,
    ): Match[][] | null => {
      if (remaining.length === 0) return [];
      if (roundIndex >= totalRounds) return null;

      const roundsLeft = totalRounds - roundIndex;
      const minMatchesTheoretical = Math.max(
        1,
        remaining.length - (roundsLeft - 1) * courts,
      );
      const maxMatchesTheoretical = Math.min(courts, remaining.length);
      const isLastRound = roundIndex === totalRounds - 1;
      let minMatches = minMatchesTheoretical;
      let maxMatches = maxMatchesTheoretical;

      if (!isLastRound) {
        minMatches = Math.max(minMatches, courts);
        maxMatches = Math.min(maxMatches, courts);
      }
      if (minMatches > maxMatches) return null;

      const mustPlay = ids.filter(
        (id) => currentState[id].idle >= MAX_RESTS_IN_ROW,
      );
      const mustRest = new Set(
        ids.filter((id) => currentState[id].play >= MAX_GAMES_IN_ROW),
      );
      if (mustPlay.length > maxMatches * 2) return null;

      const candidates: Match[][] = [];
      for (let count = maxMatches; count >= minMatches; count -= 1) {
        if (count === 1) {
          remaining.forEach((match) => candidates.push([match]));
        } else if (count === 2) {
          for (let i = 0; i < remaining.length; i += 1) {
            for (let j = i + 1; j < remaining.length; j += 1) {
              if (matchesCompatible(remaining[i], remaining[j])) {
                candidates.push([remaining[i], remaining[j]]);
              }
            }
          }
        }
      }

      for (const round of shuffleList(candidates)) {
        const playing = new Set<string>();
        let valid = true;

        for (const match of round) {
          if (mustRest.has(match.dupla1Id) || mustRest.has(match.dupla2Id)) {
            valid = false;
            break;
          }
          if (playing.has(match.dupla1Id) || playing.has(match.dupla2Id)) {
            valid = false;
            break;
          }
          playing.add(match.dupla1Id);
          playing.add(match.dupla2Id);
        }

        if (!valid) continue;
        if (mustPlay.some((id) => !playing.has(id))) continue;

        const nextState = updateState(currentState, ids, playing);
        if (!nextState) continue;

        const nextRemaining = remaining.filter(
          (match) => !round.includes(match),
        );
        const tail = backtrack(roundIndex + 1, nextRemaining, nextState);
        if (tail) return [round, ...tail];
      }

      return null;
    };

    return backtrack(0, matches, initial);
  };

  const allMatches = buildAllMatches(pairIds);
  const minRounds = Math.ceil(allMatches.length / COURTS);
  const maxRounds = minRounds + 2;

  for (
    let totalRounds = minRounds;
    totalRounds <= maxRounds;
    totalRounds += 1
  ) {
    const built = tryBuildRounds(
      shuffleList(allMatches),
      pairIds,
      COURTS,
      totalRounds,
    );
    if (built) return built;
  }

  const fallback: Match[][] = [];
  const shuffled = shuffleList(allMatches);
  for (let index = 0; index < shuffled.length; index += COURTS) {
    fallback.push(shuffled.slice(index, index + COURTS));
  }
  return fallback;
}
